import {
  CFormCheck,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DeleteIcon from "../../components/icons/DeleteIcon";
import PromptModal from "../../components/modals/PromptModal";

import MoroccanLicensePlate from "../../components/taxi/moroccan_license_plate";
import { ProfilePictureAvatar } from "../../components/taxi/Profile_picture_avatar";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import { DriverProfileStatusDisplayValue } from "../../helpers/enums";
import {
  Chip,
  Dash,
  formatDateString,
  IconAction,
  isDateApproaching,
  isEmpty,
  isNotEmpty,
} from "../../helpers/general";
import { useDriverQuery } from "../../hooks/drivers/useDriverQuery";
import { tdStyle, thStyle } from "../../helpers/constants";

interface TableProps {
  drivers: Driver[];
  DriversTableToBeDeleted: React.Dispatch<BulkDeleteActions>;
  showOnline?: boolean;
}

const COLUMNS = [
  "Chauffeur",
  "Statut",
  "Téléphone",
  "Véhicule",
  "Ville",
  "Inscription",
  "",
];

const EXPIRING_DAYS = 30;

function isDocumentExpiring(driver: Driver) {
  const profile = driver?.driverProfile;
  return [
    profile?.vehicle?.carteGriseExpiration,
    profile?.carteCinExpiration,
    profile?.permisConfianceExpiration,
    profile?.carteConducteurExpiration,
    profile?.permisExpiration,
  ]
    .filter(Boolean)
    .some((doc) => isDateApproaching(new Date(doc as string), EXPIRING_DAYS));
}

const statusTone = (status?: string) => {
  if (status === "APPROVED") return "success";
  if (status === "DECLINED" || status === "BLOCKED") return "danger";
  return "warning";
};

interface RowProps {
  driver: Driver;
  deletedList: React.Dispatch<BulkDeleteActions>;
  showOnline: boolean;
  onDelete: () => void;
}

function DriverRow({ driver, deletedList, showOnline, onDelete }: RowProps) {
  const [isChecked, setIsChecked] = useState(false);

  const toggle = useCallback(
    (checked: boolean) => {
      setIsChecked(checked);
      deletedList({
        type: checked ? DELETE_ACTIONS.ADD : DELETE_ACTIONS.REMOVE,
        id: driver.id,
      });
    },
    [deletedList, driver.id],
  );

  // A row that unmounts (page change) must not stay in the selection
  useEffect(
    () => () => deletedList({ type: DELETE_ACTIONS.REMOVE, id: driver.id }),
    [deletedList, driver.id],
  );

  const status = driver?.driverProfile?.status?.toUpperCase() as
    | keyof typeof DriverProfileStatusDisplayValue
    | undefined;
  const expiring = isDocumentExpiring(driver);
  const hasPhone = !isEmpty(driver?.phone) && driver?.phone !== "null";
  const taxiNumber = driver.driverProfile?.vehicle?.taxiNumber;

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      {/* Driver */}
      <CTableDataCell style={tdStyle}>
        <div
          className="d-flex align-items-center gap-3"
          style={{ minWidth: 240 }}
        >
          <CFormCheck
            checked={isChecked}
            onChange={(e) => toggle(e.target.checked)}
          />
          {showOnline ? (
            <ProfilePictureAvatar
              profilePicture={driver?.profilePicture}
              fullName={driver?.fullName}
              isOnline={driver?.driverProfile?.isOnline}
            />
          ) : (
            <img
              src={
                driver?.profilePicture ??
                `https://ui-avatars.com/api/?name=${encodeURIComponent(driver?.fullName || "")}`
              }
              alt=""
              width={36}
              height={36}
              className="flex-shrink-0"
              style={{ borderRadius: "50%", objectFit: "cover" }}
            />
          )}
          <div style={{ minWidth: 0 }}>
            <Link
              to={`/drivers/${driver?.id}`}
              className="fw-semibold text-capitalize d-block"
            >
              {driver?.fullName}
            </Link>
            <div
              className="text-muted text-truncate"
              style={{ fontSize: 12, maxWidth: 220 }}
            >
              {driver.email}
            </div>
            {expiring && (
              <div className="mt-1">
                <Chip tone="danger">Documents bientôt expirés</Chip>
              </div>
            )}
          </div>
        </div>
      </CTableDataCell>

      {/* Status */}
      <CTableDataCell style={tdStyle}>
        {status ? (
          <Chip tone={statusTone(status)}>
            {DriverProfileStatusDisplayValue[status]}
          </Chip>
        ) : (
          <Dash />
        )}
      </CTableDataCell>

      {/* Phone */}
      <CTableDataCell style={tdStyle}>
        {hasPhone ? (
          <div
            className="d-flex align-items-center gap-2"
            style={{ whiteSpace: "nowrap" }}
          >
            <span title={driver?.isVerifiedPhone ? "Vérifié" : "Non vérifié"}>
              {driver?.isVerifiedPhone ? "✅" : "❌"}
            </span>
            {driver.phone}
          </div>
        ) : (
          <Dash />
        )}
      </CTableDataCell>

      {/* Vehicle */}
      <CTableDataCell style={tdStyle}>
        <div style={{ minWidth: 150 }}>
          {isNotEmpty(taxiNumber) && (
            <div className="text-muted mb-1" style={{ fontSize: 12 }}>
              Taxi n°{" "}
              <span className="fw-semibold text-body">{taxiNumber}</span>
            </div>
          )}
          <MoroccanLicensePlate
            vehicleNumber={
              driver?.driverProfile?.vehicle?.immatriculation ?? ""
            }
          />
        </div>
      </CTableDataCell>

      {/* City */}
      <CTableDataCell style={tdStyle}>
        {driver?.city?.label ? <Chip>{driver.city.label}</Chip> : <Dash />}
      </CTableDataCell>

      {/* Dates */}
      <CTableDataCell style={tdStyle}>
        <div style={{ whiteSpace: "nowrap" }}>
          {formatDateString(driver.createdAt)}
        </div>
        {driver?.birthday && (
          <div
            className="text-muted"
            style={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            Né(e) le {formatDateString(driver.birthday)}
          </div>
        )}
      </CTableDataCell>

      {/* Actions */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex justify-content-end">
          <IconAction title="Supprimer" danger onClick={onDelete}>
            <DeleteIcon width="18" height="18" fill={colors.primary} />
          </IconAction>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

export default function DriversTable({
  drivers,
  DriversTableToBeDeleted,
  showOnline = true,
}: TableProps) {
  // One hook for the whole table instead of one per row
  const { removeDriver } = useDriverQuery();
  const [driverToDelete, setDriverToDelete] = useState<Driver | null>(null);

  return (
    <div>
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            {COLUMNS.map((label, i) => (
              <CTableHeaderCell key={i} scope="col" style={thStyle}>
                {label}
              </CTableHeaderCell>
            ))}
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {drivers?.map((driver) => (
            <DriverRow
              key={driver.id}
              driver={driver}
              deletedList={DriversTableToBeDeleted}
              showOnline={showOnline}
              onDelete={() => setDriverToDelete(driver)}
            />
          ))}
        </CTableBody>
      </CTable>

      <PromptModal
        open={driverToDelete !== null}
        onAccept={() => {
          if (driverToDelete) removeDriver(driverToDelete.id);
          setDriverToDelete(null);
        }}
        onClose={() => setDriverToDelete(null)}
      />
    </div>
  );
}
